import { Box, Button, Checkbox, Divider, InputAdornment, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, MenuList, TextField, Typography } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import React from 'react';
import Utils from '@utils/index';
import { extractKeywords, matchesKeywordPeriod } from '@utils/filtering/filter-utils';
import SearchHeaderTooltipIcon from '@components/common/SearchHeaderTooltipIcon';

type CompanySelectorProps = {
  availableCompanies: Company[];
  selectedCompanies: Company[];
  onSelectionChange: (companies: Company[]) => void;
  disabled: boolean;
}

const CompanySelector: React.FC<CompanySelectorProps> = (props) => {
  const { selectedCompanies, availableCompanies, onSelectionChange, disabled } = props;

  const [anchorEl, setAnchorEl] = React.useState<any | null>(null);
  const [filterText, setFilterText] = React.useState<string>("");
  const [finalCompanies, setFinalCompanies] = React.useState<Company[]>(availableCompanies);

  React.useEffect(() => {
    const keywords = extractKeywords(filterText);
    const filtered = availableCompanies.filter((company) => matchesKeywordPeriod(keywords, company, ["name"]))
    setFinalCompanies(filtered);
  }, [filterText, availableCompanies])

  function getCompaniesText(): string {
    if (selectedCompanies.length === availableCompanies.length) return "All";
    else return `${selectedCompanies.length}/${availableCompanies.length} companies`;
  }

  return (
    <Box width="100%">
      <Button
        disabled={disabled}
        variant="outlined"
        endIcon={<KeyboardArrowDown />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        data-cy="select-company-button"
        color="secondary"
      >
        <Typography style={{ width: '7rem' }} noWrap={true} variant="button">{getCompaniesText()}</Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setTimeout(() => { setFilterText("") }, 300)
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuList disablePadding>
          <ListSubheader style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }} >
            <TextField
              style={{ marginTop: 0 }}
              autoFocus={true}
              placeholder="Search"
              variant="standard"
              defaultValue={filterText}
              onChange={(event) => { event.stopPropagation(); event.preventDefault(); setFilterText(event.target.value); }}
              onKeyDown={(e) => { e.stopPropagation(); }}
              size="medium"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchHeaderTooltipIcon />
                  </InputAdornment>
                ),
              }}
              data-cy="company-list-search-input"
            />
          </ListSubheader>
        </MenuList>
        <Divider style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />
        <MenuItem
          style={{ paddingTop: 0, paddingBottom: 0 }}
          dense
          data-cy={`company-selector-all-companies-menu-item`}
          onClick={() => {
            if (selectedCompanies.length === availableCompanies.length) {
              onSelectionChange([])
            } else {
              onSelectionChange([...availableCompanies]);
            }
          }}
        >
          <ListItemIcon
            style={{ minWidth: "2.25rem" }}
          >
            <Checkbox
              color="primary"
              size="small"
              checked={Boolean(selectedCompanies.length === availableCompanies.length)}
            />
          </ListItemIcon>
          <ListItemText>
            <Typography noWrap={true} style={{ width: '15rem' }}>All companies</Typography>
          </ListItemText>
        </MenuItem>
        <Divider style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />
        <MenuList disablePadding style={{ maxHeight: '40vh', overflowY: 'auto' }}>
          {Utils.Array.orderAlphabetically(finalCompanies, 'name').map(company => (
            <MenuItem dense style={{ paddingTop: 0, paddingBottom: 0 }} key={company.companyId} data-cy={`company-selector-${company?.name}-menu-item`}
              onClick={() => {
                if (Boolean(selectedCompanies.find(c => c.companyId === company.companyId)))
                  onSelectionChange(selectedCompanies.filter(el => el.companyId !== company.companyId))
                else
                  onSelectionChange([...selectedCompanies, company].sort((c1, c2) => (c1?.name || "z").localeCompare((c2?.name || "z"))));
              }}>
              <ListItemIcon
                style={{ minWidth: "2.25rem" }}
              >
                <Checkbox
                  color="primary"
                  size="small"
                  checked={Boolean(selectedCompanies.find(c => c.companyId === company.companyId))}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography noWrap={true} style={{ width: '15rem' }}>{company.name}</Typography>
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  )
}

export default CompanySelector;
